import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  House,
  UsersThree,
  ReceiptX,
  Wallet,
  ShieldCheck,
  ClockCounterClockwise,
  ChartBar,
  SignOut,
  List as ListIcon,
  CashRegister,
  X,
  GearSix,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";

// Shared nav for admin + manager (was duplicated)
const BACK_OFFICE_NAV = [
  {
    label: "Caisse & Danseuses",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: House },
      { to: "/daily-cash", label: "Compte du jour", icon: CashRegister },
      { to: "/dancers", label: "Danseuses", icon: UsersThree },
      { to: "/payment-requests", label: "Demandes de règlement", icon: ReceiptX },
      { to: "/payments", label: "Règlements & factures", icon: Wallet },
    ],
  },
  {
    label: "Personnel",
    items: [
      { to: "/staff", label: "Personnel", icon: ShieldCheck },
      { to: "/timesheets", label: "Pointages", icon: ClockCounterClockwise },
      { to: "/reports", label: "Rapports", icon: ChartBar },
    ],
  },
];

// Admin-only extra section
const ADMIN_EXTRAS = {
  label: "Administration",
  items: [{ to: "/settings", label: "Réglages", icon: GearSix, adminOnly: true }],
};

const NAV = {
  admin: [...BACK_OFFICE_NAV, ADMIN_EXTRAS],
  manager: BACK_OFFICE_NAV,
  dancer: [{ label: null, items: [{ to: "/portail", label: "Mon espace", icon: House }] }],
};

const ROLE_LABEL = { admin: "Patron", manager: "Manageuse", dancer: "Danseuse" };

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Auto-close sidebar on route change (mobile)
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (!user) return null;
  const blocks = NAV[user.role] || [];

  return (
    <div className="min-h-screen flex" style={{ background: "#09090b" }}>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:sticky top-0 left-0 z-40 h-screen w-64 shrink-0 border-r overflow-y-auto transition-transform duration-200`}
        style={{ borderColor: "#27272a", background: "#09090b" }}
      >
        <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: "#27272a" }}>
          <div>
            <div className="font-display text-2xl font-medium tracking-tight">Gestion Tantra</div>
            <div className="label-xs mt-1">Back-office</div>
          </div>
          <button
            className="md:hidden text-[#71717a] hover:text-white"
            onClick={() => setOpen(false)}
            data-testid="sidebar-close"
            aria-label="Fermer"
          >
            <X size={22} />
          </button>
        </div>

        <nav className="p-4 pb-36 space-y-5">
          {blocks.map((block) => (
            <div key={block.label || "nav-default"} className="space-y-1">
              {block.label && (
                <div className="label-xs px-3 pb-2 text-[#52525b]">{block.label}</div>
              )}
              {block.items.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    data-testid={`nav-${it.to.replace("/", "")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                        isActive
                          ? "bg-[#1a1a21] text-white border border-[#27272a]"
                          : "text-[#a1a1aa] hover:bg-[#121217] hover:text-white"
                      }`
                    }
                  >
                    <Icon size={18} weight="regular" />
                    <span>{it.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div
          className="fixed md:absolute bottom-0 left-0 w-64 p-4 border-t"
          style={{ borderColor: "#27272a", background: "#09090b" }}
        >
          <div className="text-sm font-medium truncate">{user.full_name}</div>
          <div className="label-xs mt-1">{ROLE_LABEL[user.role]}</div>
          <Button
            data-testid="logout-btn"
            onClick={logout}
            variant="outline"
            className="mt-3 w-full border-[#27272a] text-[#a1a1aa] hover:bg-[#1a1a21] hover:text-white"
          >
            <SignOut size={16} className="mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div
          className="md:hidden sticky top-0 z-20 flex items-center justify-between p-4 border-b"
          style={{ borderColor: "#27272a", background: "rgba(9,9,11,0.85)", backdropFilter: "blur(12px)" }}
        >
          <button data-testid="sidebar-toggle" onClick={() => setOpen(true)} className="text-white" aria-label="Ouvrir le menu">
            <ListIcon size={24} />
          </button>
          <div className="font-display text-lg">Gestion Tantra</div>
          <div className="w-6" />
        </div>
        <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
